/**
 * DevThemePanel — 테마 전환 패널 (DEV 전용)
 *
 * 왜: 테마를 바꿔보려면 로그인 → 설정 → 변경 → 로그아웃을 반복해야 했다. 로그인 화면처럼
 * **로그아웃 상태의 화면**을 테마별로 확인하는 게 특히 고통스러웠다.
 *
 * 클래스만 토글하면 안 된다 — ThemeApplicator 가 스토어를 보고 root class 를 다시 덮어쓴다.
 * 그래서 **실제 스토어(useGlobalStore)를 건드린다** → zustand persist(localStorage) 로 살아남고,
 * 로그아웃 상태에서도 유지되며, 설정 UI 와 완전히 같은 경로를 탄다(= 이 패널로 본 게 진짜 모습).
 *
 * 스와치 색은 하드코딩하지 않고 **런타임에 실제 CSS 에서 읽는다**(테마 파일이 바뀌면 자동 반영,
 * 목록이 거짓말하지 않는다).
 *
 * @module core/dev/DevThemePanel
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Palette, Check, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "~/core/utils/cn";
import { useGlobalStore, type BuiltInDesignStyle } from "~/core/store/global/useGlobalStore";
import { THEMES, getThemeClassName } from "~/core/config/themes";

type Probe = { bg: string; accent: string; text: string };

/** 테마 클래스를 붙인 요소의 계산색을 실제로 읽는다(하드코딩 금지 — 테마 파일이 진실). */
function probeTheme(themeClass: string, dark: boolean): Probe {
  const el = document.createElement("div");
  el.className = cn(themeClass, dark && "dark");
  el.style.position = "fixed";
  el.style.opacity = "0";
  el.style.pointerEvents = "none";
  document.body.appendChild(el);
  const cs = getComputedStyle(el);
  const probe: Probe = {
    bg: cs.getPropertyValue("--bg-main").trim() || "#fff",
    accent: cs.getPropertyValue("--accent-color").trim() || "#888",
    text: cs.getPropertyValue("--text-main").trim() || "#000",
  };
  el.remove();
  return probe;
}

/**
 * ⚠️ 다크는 스토어의 **`isDarkMode`(불리언)** 이 진짜 스위치다(ThemeApplicator 가 이걸 보고
 * root 에 .dark 를 붙인다). `theme: 'light'|'dark'|'system'` 은 **연결되지 않은 별도 선호도**라
 * setTheme 만 부르면 다크가 안 켜진다(실제로 그래서 안 켜졌다).
 * 'system' 은 앱에 구현이 없다 → OS 선호를 읽어 한 번 반영하는 것으로 대신한다.
 */
const MODES: { value: "light" | "dark" | "system"; label: string; Icon: typeof Sun }[] = [
  { value: "light", label: "라이트", Icon: Sun },
  { value: "dark", label: "다크", Icon: Moon },
  { value: "system", label: "시스템", Icon: Monitor },
];

export function DevThemePanel() {
  const [open, setOpen] = useState(false);
  const theme = useGlobalStore((s) => s.theme);
  const isDarkMode = useGlobalStore((s) => s.isDarkMode);
  const designStyle = useGlobalStore((s) => s.designStyle);
  const setTheme = useGlobalStore((s) => s.setTheme);
  const setDarkMode = useGlobalStore((s) => s.setDarkMode);
  const setDesignStyle = useGlobalStore((s) => s.setDesignStyle);


  /** 선호도(theme)와 실제 스위치(isDarkMode)를 **함께** 갱신한다 — 하나만 바꾸면 안 먹는다. */
  const applyMode = useCallback(
    (m: "light" | "dark" | "system") => {
      setTheme(m);
      const dark =
        m === "system"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
          : m === "dark";
      setDarkMode(dark);
    },
    [setTheme, setDarkMode],
  );

  // 내장 테마만(커스텀 테마는 설정에서 관리)
  const builtIns = useMemo(
    () => THEMES.filter((t) => !String(t.id).startsWith("custom-")),
    [],
  );

  // URL 파라미터 진입 — `?theme=neon&mode=dark`. 칩을 못 찾아도(또는 HMR 이 꼬여 칩이 안 떠도)
  // 주소만으로 테마를 볼 수 있다. 스샷을 특정 테마로 찍거나 링크로 공유할 때도 쓴다.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("theme");
    const m = p.get("mode");
    if (t && builtIns.some((b) => b.id === t)) setDesignStyle(t as BuiltInDesignStyle);
    if (m === "light" || m === "dark" || m === "system") applyMode(m);
  }, [builtIns, setDesignStyle, applyMode]);

  // 스와치 재측정용 — root 의 class 가 바뀌면 다시 잰다.
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains("dark"));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  // 패널을 열 때만 측정(닫혀 있으면 DOM 프로브 비용 0). 다크 상태가 바뀌면 다시 측정.
  const [probes, setProbes] = useState<Record<string, Probe>>({});
  useEffect(() => {
    if (!open) return;
    const next: Record<string, Probe> = {};
    for (const t of builtIns) {
      next[String(t.id)] = probeTheme(getThemeClassName(t.id), isDark);
    }
    setProbes(next);
  }, [open, isDark, builtIns]);

  const current = builtIns.find((t) => t.id === designStyle);
  const pick = useCallback(
    (id: BuiltInDesignStyle) => setDesignStyle(id),
    [setDesignStyle],
  );

  return (
    // 위치를 스스로 잡는다(부모 의존 금지 — root HMR 실패 시 증발 방지).
    // left 오프셋은 페르소나 칩(≈118px) 오른쪽. 겹쳐도 치명적이지 않다.
    <div className="fixed bottom-3 left-[8.75rem] z-[60] select-none font-sans">
      {/* 펼친 패널 — 칩 위로 열린다 */}
      {open && (
        <div className="absolute bottom-full mb-2 left-0 w-64 rounded-xl border border-line bg-elevated shadow-2xl overflow-hidden">
          {/* 라이트/다크/시스템 */}
          <div className="p-2 border-b border-hairline">
            <p className="text-[10px] font-semibold text-muted px-1 mb-1.5">모드</p>
            <div className="flex gap-1">
              {MODES.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => applyMode(value)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors",
                    theme === value
                      ? "bg-accent text-on-accent"
                      : "bg-card text-muted hover:bg-emphasis",
                  )}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 내장 테마 */}
          <div className="p-2 max-h-64 overflow-y-auto">
            <p className="text-[10px] font-semibold text-muted px-1 mb-1.5">테마</p>
            <div className="grid grid-cols-2 gap-1">
              {builtIns.map((t) => {
                const id = String(t.id);
                const p = probes[id];
                const active = t.id === designStyle;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => pick(t.id as BuiltInDesignStyle)}
                    title={t.description}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors",
                      active ? "bg-accent/15 ring-1 ring-accent" : "hover:bg-emphasis",
                    )}
                  >
                    {/* 스와치 — 테마의 실제 바탕 + accent 를 런타임에 읽어 그린다 */}
                    <span
                      className="w-5 h-5 rounded-md border border-line shrink-0 flex items-end justify-end p-[2px]"
                      style={p ? { backgroundColor: p.bg } : undefined}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={p ? { backgroundColor: p.accent } : undefined}
                      />
                    </span>
                    <span className="min-w-0 flex-1 text-[11px] font-medium text-main truncate">
                      {t.name}
                    </span>
                    {active && <Check className="w-3 h-3 text-accent shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="px-3 py-1.5 border-t border-hairline text-[10px] text-subtle">
            실제 설정과 같은 스토어 — 로그아웃해도 유지된다
          </p>
        </div>
      )}

      {/* 토글 칩 */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="테마 전환 (DEV)"
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-lg border border-line",
          "bg-elevated hover:bg-emphasis text-main transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-accent",
        )}
      >
        <Palette className="w-3.5 h-3.5 text-accent" />
        <span className="text-[11px] font-semibold">{current?.name ?? "테마"}</span>
        <span className="text-[9px] text-muted">{isDarkMode ? "다크" : "라이트"}</span>
      </button>
    </div>
  );
}

export default DevThemePanel;
